import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import ReadOnlyListPanel from "../../components/ReadOnlyListPanel";
import BlockedSurface from "../../components/BlockedSurface";
import {
  ADMIN_MODELS,
  CLASSIC_SITE_BASE,
  DJANGO_ADMIN_URL,
  SURFACE_STATUS,
} from "../../classicSite.constants";
import {
  getPublicEvents,
  getPublicPromos,
  getSocialGroups,
  getTagGroupings,
  getTags,
} from "../../api/classicSiteHoodieApi";

export default function AdminHomePage() {
  return (
    <div className="cs-admin">
      <h1>Admin parity (classic_site/admin.py)</h1>
      <p>
        No REST admin API. Shell pages document Django admin configuration and
        link to existing staff tools where overlap exists.
      </p>
      <table className="cs-table">
        <thead>
          <tr>
            <th>Model</th>
            <th>Django model</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {ADMIN_MODELS.map((model) => (
            <tr key={model.slug}>
              <td>{model.label}</td>
              <td>
                <code>{model.djangoModel}</code>
              </td>
              <td>
                <span className={`cs-badge cs-badge--${model.status}`}>
                  {model.status}
                </span>
              </td>
              <td>
                <Link to={`${CLASSIC_SITE_BASE}/admin/${model.slug}`}>Open</Link>
                {model.adminToolsPath ? (
                  <>
                    {" · "}
                    <Link to={model.adminToolsPath}>Admin tools</Link>
                  </>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="cs-admin__django">
        Full CRUD:{" "}
        <a href={DJANGO_ADMIN_URL} target="_blank" rel="noopener noreferrer">
          Django admin
        </a>
      </p>
    </div>
  );
}

function AdminBlockedModelPage({ model }) {
  return (
    <BlockedSurface
      title={model.label}
      status={model.status}
      djangoUrl={`${DJANGO_ADMIN_URL}classic_site/${model.slug.replace(/-/g, "").replace("subscriptionusers", "subscriptionuser")}/`}
      reason={`No admin REST API for ${model.djangoModel}. Django admin is the authoritative surface.`}
      specRef="classic-site-react-admin-parity-matrix-v1.md"
    >
      {model.adminToolsPath ? (
        <p>
          Partial overlap:{" "}
          <Link to={model.adminToolsPath}>{model.adminToolsPath}</Link>
        </p>
      ) : null}
    </BlockedSurface>
  );
}

export function AdminListingsPage() {
  const model = ADMIN_MODELS.find((m) => m.slug === "listings");
  return (
    <>
      <AdminBlockedModelPage model={model} />
      <p>
        Staff listing wrangler:{" "}
        <Link to="/admin-tools/listings">/admin-tools/listings</Link>
      </p>
    </>
  );
}

export function AdminTagsPage() {
  const load = useCallback(() => getTags(), []);
  return (
    <>
      <ReadOnlyListPanel
        title="Listing tags (read-only hoodie API)"
        load={load}
        render={(rows) => (
          <ul className="cs-list">
            {(rows || []).map((tag) => (
              <li key={tag.id}>{tag.name}</li>
            ))}
          </ul>
        )}
      />
      <BlockedSurface
        title="Admin tag writes"
        status={SURFACE_STATUS.BLOCKED}
        djangoUrl={`${DJANGO_ADMIN_URL}classic_site/listingtag/`}
        reason="Tag CRUD requires Django model permissions — not verified for staff via hoodie API from this silo."
        specRef="classic-site-react-admin-parity-matrix-v1.md — ListingTag"
      />
    </>
  );
}

export function AdminTagGroupingsPage() {
  const load = useCallback(() => getTagGroupings(), []);
  return (
    <>
      <ReadOnlyListPanel
        title="Tag groupings (read-only)"
        load={load}
        render={(rows) => (
          <ul className="cs-list">
            {(rows || []).map((g) => (
              <li key={g.id}>{g.name}</li>
            ))}
          </ul>
        )}
      />
      <BlockedSurface
        title="Admin tag grouping writes"
        status={SURFACE_STATUS.BLOCKED}
        djangoUrl={`${DJANGO_ADMIN_URL}classic_site/taggrouping/`}
        reason="No admin REST API."
        specRef="classic-site-react-admin-parity-matrix-v1.md"
      />
    </>
  );
}

export function AdminSocialGroupsPage() {
  const load = useCallback(() => getSocialGroups(), []);
  return (
    <>
      <ReadOnlyListPanel
        title="Facebook groups (read-only)"
        load={load}
        render={(rows) => (
          <ul className="cs-list">
            {(rows || []).map((g) => (
              <li key={g.id}>{g.name}</li>
            ))}
          </ul>
        )}
      />
      <BlockedSurface
        title="Admin social group writes"
        status={SURFACE_STATUS.BLOCKED}
        djangoUrl={`${DJANGO_ADMIN_URL}classic_site/facebookgroup/`}
        reason="No admin REST API."
        specRef="classic-site-react-admin-parity-matrix-v1.md"
      />
    </>
  );
}

export function AdminEventsPage() {
  const load = useCallback(() => getPublicEvents(), []);
  return (
    <>
      <ReadOnlyListPanel
        title="Events (public queryset — not admin list)"
        load={load}
        render={(rows) => (
          <p>{(rows || []).length} upcoming active events in API queryset.</p>
        )}
      />
      <BlockedSurface
        title="Admin event CRUD"
        status={SURFACE_STATUS.BLOCKED}
        djangoUrl={`${DJANGO_ADMIN_URL}classic_site/listingevent/`}
        reason="Admin needs inactive/past/all-entry events. API filters active future only."
        specRef="classic-site-react-admin-parity-matrix-v1.md — ListingEvent"
      />
    </>
  );
}

export function AdminPromosPage() {
  const load = useCallback(() => getPublicPromos(), []);
  return (
    <>
      <ReadOnlyListPanel
        title="Promos (public queryset — not admin list)"
        load={load}
        render={(rows) => (
          <p>{(rows || []).length} active promos in API queryset.</p>
        )}
      />
      <BlockedSurface
        title="Admin promo CRUD"
        status={SURFACE_STATUS.BLOCKED}
        djangoUrl={`${DJANGO_ADMIN_URL}classic_site/listingpromotion/`}
        reason="Admin needs inactive promos. API returns active only."
        specRef="classic-site-react-admin-parity-matrix-v1.md — ListingPromotion"
      />
    </>
  );
}

export function AdminGenericBlockedPage({ slug }) {
  const model =
    ADMIN_MODELS.find((m) => m.slug === slug) ||
    ({
      slug,
      label: slug,
      djangoModel: "unknown",
      status: SURFACE_STATUS.BLOCKED,
    });
  return <AdminBlockedModelPage model={model} />;
}
